import React, { useState, useEffect } from 'react';
import { LanguageToggle } from './LanguageToggle';
import { useLanguage } from '../i18n/useLanguage';

interface ComparisonModeSelectorProps {
  onSelectCloud: () => void;
  onSelectWorkplace: () => void;
  onOpenRegulatory: () => void;
}

type ExpandedSide = null | 'cloud' | 'workplace';

const MOBILE_BREAKPOINT = 768;

export const ComparisonModeSelector: React.FC<ComparisonModeSelectorProps> = ({
  onSelectCloud,
  onSelectWorkplace,
  onOpenRegulatory,
}) => {
  const [expanded, setExpanded] = useState<ExpandedSide>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { language, t } = useLanguage();

  // Preload both background images
  useEffect(() => {
    let loaded = 0;
    const onLoad = () => { loaded++; if (loaded >= 2) setImagesLoaded(true); };
    const img1 = new Image(); img1.onload = onLoad; img1.onerror = onLoad; img1.src = '/clouds.png';
    const img2 = new Image(); img2.onload = onLoad; img2.onerror = onLoad; img2.src = '/arbeitsplatz.png';
    // Fallback: show after 4s even if images fail
    const timer = setTimeout(() => setImagesLoaded(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleClick = (side: 'cloud' | 'workplace') => {
    if (expanded) return;
    setExpanded(side);
  };

  const handleStart = () => {
    if (expanded === 'cloud') onSelectCloud();
    else if (expanded === 'workplace') onSelectWorkplace();
  };

  const handleBack = () => setExpanded(null);

  // Desktop: translateX, panels side by side
  // Mobile: translateY, panels stacked top/bottom
  const getTransform = (side: 'cloud' | 'workplace'): string => {
    if (isMobile) {
      // Mobile: vertical layout
      if (side === 'cloud') {
        if (expanded === null) return 'translateY(-50vh)';
        if (expanded === 'cloud') return 'translateY(0)';
        return 'translateY(-100vh)';
      } else {
        if (expanded === null) return 'translateY(50vh)';
        if (expanded === 'workplace') return 'translateY(0)';
        return 'translateY(100vh)';
      }
    } else {
      // Desktop: horizontal layout
      if (side === 'cloud') {
        if (expanded === null) return 'translateX(-50vw)';
        if (expanded === 'cloud') return 'translateX(0)';
        return 'translateX(-100vw)';
      } else {
        if (expanded === null) return 'translateX(50vw)';
        if (expanded === 'workplace') return 'translateX(0)';
        return 'translateX(100vw)';
      }
    }
  };

  const getTextOffset = (side: 'cloud' | 'workplace'): string => {
    if (expanded !== null) return 'translate(0, 0)';
    if (isMobile) {
      return side === 'cloud' ? 'translateY(25vh)' : 'translateY(-25vh)';
    }
    return side === 'cloud' ? 'translateX(25vw)' : 'translateX(-25vw)';
  };

  const panelStyle = (side: 'cloud' | 'workplace'): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundImage: `url(/${side === 'cloud' ? 'clouds.png' : 'arbeitsplatz.png'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: expanded ? 'default' : 'pointer',
    transform: getTransform(side),
    transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: expanded === side ? 2 : 1,
  });

  const overlay: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
  };

  const textBox = (side: 'cloud' | 'workplace'): React.CSSProperties => ({
    position: 'relative',
    zIndex: 2,
    color: '#fff',
    textAlign: 'center',
    padding: isMobile ? '0 20px' : '0 32px',
    maxWidth: isMobile ? 340 : 420,
    transform: getTextOffset(side),
    transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
  });

  const titleSize = isMobile ? 26 : 32;
  const titleSizeExpanded = isMobile ? 32 : 40;
  const descSize = isMobile ? 14 : 16;
  const descSizeExpanded = isMobile ? 16 : 18;
  const btnPad = isMobile ? '14px 36px' : '16px 48px';
  const btnFont = isMobile ? 16 : 18;

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: '#000' }}>

      {/* Loading screen */}
      {!imagesLoaded && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 100,
          background: '#fff',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 36, height: 36, border: '3px solid #eee', borderTopColor: '#2C8F92',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Main content — fades in when loaded */}
      <div style={{ opacity: imagesLoaded ? 1 : 0, transition: 'opacity 0.6s ease' }}>
      {/* Cloud Panel */}
      <div
        onClick={() => handleClick('cloud')}
        style={panelStyle('cloud')}
        role="button"
        tabIndex={0}
        aria-label="Cloud Vergleich"
        onKeyDown={(e) => e.key === 'Enter' && handleClick('cloud')}
      >
        <div style={overlay} />
        <div style={textBox('cloud')}>
          <h2 style={{ fontSize: expanded === 'cloud' ? titleSizeExpanded : titleSize, fontWeight: 800, margin: '0 0 12px', textShadow: '0 2px 8px rgba(0,0,0,0.5)', transition: 'font-size 0.5s ease' }}>
            {t('modeSelector.cloud.title')}
          </h2>
          <p style={{ fontSize: expanded === 'cloud' ? descSizeExpanded : descSize, lineHeight: 1.6, margin: expanded === 'cloud' ? '0 0 32px' : '0', textShadow: '0 1px 4px rgba(0,0,0,0.5)', transition: 'all 0.5s ease' }}>
            {t('modeSelector.cloud.description')}
          </p>
          {expanded === 'cloud' && (
            <>
              <button onClick={handleStart} style={{ background: '#F7675E', color: '#fff', border: 'none', padding: btnPad, fontSize: btnFont, fontWeight: 700, borderRadius: 8, cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', transition: 'transform 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
                Start
              </button>
              <button onClick={handleBack} style={{ display: 'block', margin: '16px auto 0', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.5)', padding: '8px 24px', fontSize: 14, borderRadius: 6, cursor: 'pointer' }}>
                ← {language === 'de' ? 'Zurück' : 'Back'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Workplace Panel */}
      <div
        onClick={() => handleClick('workplace')}
        style={panelStyle('workplace')}
        role="button"
        tabIndex={0}
        aria-label="Workplace Vergleich"
        onKeyDown={(e) => e.key === 'Enter' && handleClick('workplace')}
      >
        <div style={overlay} />
        <div style={textBox('workplace')}>
          <h2 style={{ fontSize: expanded === 'workplace' ? titleSizeExpanded : titleSize, fontWeight: 800, margin: '0 0 12px', textShadow: '0 2px 8px rgba(0,0,0,0.5)', transition: 'font-size 0.5s ease' }}>
            {t('modeSelector.workplace.title')}
          </h2>
          <p style={{ fontSize: expanded === 'workplace' ? descSizeExpanded : descSize, lineHeight: 1.6, margin: expanded === 'workplace' ? '0 0 32px' : '0', textShadow: '0 1px 4px rgba(0,0,0,0.5)', transition: 'all 0.5s ease' }}>
            {t('modeSelector.workplace.description')}
          </p>
          {expanded === 'workplace' && (
            <>
              <button onClick={handleStart} style={{ background: '#2C8F92', color: '#fff', border: 'none', padding: btnPad, fontSize: btnFont, fontWeight: 700, borderRadius: 8, cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', transition: 'transform 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}>
                Start
              </button>
              <button onClick={handleBack} style={{ display: 'block', margin: '16px auto 0', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.5)', padding: '8px 24px', fontSize: 14, borderRadius: 6, cursor: 'pointer' }}>
                ← {language === 'de' ? 'Zurück' : 'Back'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Language Toggle - top right */}
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 20 }}>
        <LanguageToggle style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '4px 8px', backdropFilter: 'blur(4px)' }} />
      </div>

      {/* Footer */}
      {!expanded && (
        <div style={{ position: 'fixed', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 16, zIndex: 10 }}>
          <a href="/impressum.html" style={{ color: '#fff', fontSize: 12, textDecoration: 'none', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Impressum</a>
          <a href="/datenschutz.html" style={{ color: '#fff', fontSize: 12, textDecoration: 'none', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Datenschutz</a>
        </div>
      )}
      </div>
    </div>
  );
};
